// Billing (platform admin) — who's paying: every subscription with its company, tier, status,
// billing period and dates (PayFast is the live provider) — PLUS full management: drill into a
// subscription to see its invoices/payments/history and change tier, cancel, reactivate or grant
// free access; and a Tiers tab to manage the pricing catalogue itself.
//
// Two sub-tabs (Subscriptions / Tiers) + a signal-swapped detail panel — all ONE screen, no new
// route. Every signal is read at the TOP of the one `screenBody()` reactive slot (nested
// ${() => …} blocks APPEND on re-render in this tina4js build — see CLAUDE.md); the search input
// and per-page select stay in the STATIC template (toggled via ?hidden) so a debounced reload
// never recreates the element the admin is typing into.
import { html, signal } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { shell } from './shell.js';
import { pager, perPageSelect, DEFAULT_PER_PAGE } from '../shared/pager.js';
import { icon } from '../shared/icons.js';
import { focusModal } from '../shared/modal.js';
import { t } from '../shared/i18n.js';
import { isPartnerAdmin, isPlatformAdmin } from '../shared/auth.js';
import { fmtAmountOnly } from '../tenant/bank_helpers.js';

// Tiers are the GLOBAL pricing catalogue — not a Regional Partner's to manage. They see subscriptions
// (per-company, region-scoped by the API) only; the Tiers tab is hidden for them.
const _regionalPartner = () => isPartnerAdmin() && !isPlatformAdmin();

export const SUB_BADGE = { active: 'success', trialing: 'info', cancelled: 'danger',
                    past_due: 'warning', expired: 'secondary' };

// ---- pure helpers (exported so they can be unit-tested directly, no DOM needed) ----

// Badge colour class for a subscription status — unknown statuses fall back to 'secondary'.
export function subBadgeClass(status){ return SUB_BADGE[status] || 'secondary'; }

// Badge TEXT for a subscription status — 'trialing' reads as "Trial"; every other status shows
// exactly as stored. (Owner 2026-09-07: the status should say "Trial", not "trialing".)
export function subStatusLabel(status){
  return status === 'trialing' ? t('billing.status_trial', 'Trial') : (status || '—');
}

// The date to show for a "cancel at period end" subscription, or '' when the hint doesn't apply.
export function subEndsDate(sub){
  if(!sub || !sub.cancel_at_period_end) return '';
  return sub.period_end || sub.current_period_end || '';
}

// Is the Reactivate action offered for this subscription?
export function canReactivate(sub){
  return !!sub && (sub.status === 'cancelled' || !!sub.cancel_at_period_end);
}

// A short price display line for a tier — tolerant of whatever shape the API returns
// (price_month / price_year per billing.tier_product, or a generic `price`). '' when unpriced.
// `sym` is the tier's own currency symbol (a tier is priced in its country's currency) — never a
// literal R (owner 2026-10-19).
export function tierPriceLine(tier, sym = ''){
  if(!tier) return '';
  const pre = sym ? sym + ' ' : '';
  const parts = [];
  if(tier.price_month != null && tier.price_month !== '') parts.push(pre + tier.price_month + ' / mo');
  if(tier.price_year != null && tier.price_year !== '') parts.push(pre + tier.price_year + ' / yr');
  if(!parts.length && tier.price != null && tier.price !== '') parts.push(pre + tier.price);
  return parts.join(' · ');
}

// The tier's usage caps as a short line ("≤ 500 txns · ≤ R 1000000 turnover · ≤ 5 users"), or ''.
export function tierCapsLine(tier, sym = ''){
  if(!tier) return '';
  const parts = [];
  if(tier.max_txn_count != null && tier.max_txn_count !== '') parts.push('≤ ' + tier.max_txn_count + ' txns');
  if(tier.max_turnover != null && tier.max_turnover !== '') parts.push('≤ ' + (sym ? sym + ' ' : '') + tier.max_turnover + ' turnover');
  if(tier.max_users != null && tier.max_users !== '') parts.push('≤ ' + tier.max_users + ' users');
  return parts.join(' · ');
}

// Active flag tolerant of both shapes (billing.tier_product has is_active).
export function tierIsActive(tier){
  if(!tier) return false;
  return (tier.is_active != null ? tier.is_active : tier.active) !== false;
}

export function billingScreen(){
  // ---- tab + view state ----
  const tab  = signal('subs');    // 'subs' | 'tiers'
  const view = signal('list');    // 'list' | 'detail'

  // ---- Subscriptions list ----
  const rows = signal([]), total = signal(0), page = signal(1), pages = signal(1),
        perPage = signal(DEFAULT_PER_PAGE), loading = signal(true);
  const statusFilter = signal(''), stats = signal(null);   // W9: status chips + KPI band
  let timer, lastQ = '', loadSeq = 0;   // loadSeq: race-safety — drop a stale response

  async function load(){
    const seq = ++loadSeq;
    loading.value = true;
    const p = new URLSearchParams({ per_page: perPage.value, page: String(page.value) });
    if(lastQ.trim()) p.set('q', lastQ.trim());
    if(statusFilter.value) p.set('status', statusFilter.value);
    try {
      const r = await api.get('/admin/billing?' + p);
      if(seq !== loadSeq) return;                        // a newer load already landed — drop this
      rows.value = r.data || []; total.value = r.total || 0; pages.value = r.total_pages || 1;
    } catch {
      if(seq !== loadSeq) return;
      rows.value = []; total.value = 0; pages.value = 1;
    }
    if(seq === loadSeq) loading.value = false;
  }
  // KPI band — platform-wide subscription counts via the same list endpoint (per_page=1 → total).
  // Fired once on mount; a status chip narrows the LIST, not the cards.
  async function loadStats(){
    const count = qs => api.get('/admin/billing?per_page=1' + (qs ? '&' + qs : ''))
      .then(r => (r && r.total) || 0).catch(() => null);
    const [all, active, trialing, past_due, cancelled] = await Promise.all([
      count(''), count('status=active'), count('status=trialing'),
      count('status=past_due'), count('status=cancelled'),
    ]);
    stats.value = { all, active, trialing, past_due, cancelled };
  }
  load(); loadStats();
  function onSearch(e){ lastQ = e.target.value; page.value = 1; clearTimeout(timer); timer = setTimeout(load, 220); }
  function onPer(e){ perPage.value = e.target.value; page.value = 1; load(); }
  function goPage(p){ page.value = p; load(); }
  function setStatus(k){ statusFilter.value = k; page.value = 1; load(); }

  // ---- Tiers tab ----
  const tiers = signal([]), tiersLoading = signal(true), tiersErr = signal(''), currencies = signal([]);
  const tierSymbol = tr => { const c = currencies.value.find(x => String(x.id) === String(tr.currency_id)); return c ? c.symbol : ''; };
  // a billing figure with ITS currency's mark (an invoice's / its payment's), the bare figure when unknown
  const moneyIn = (v, currencyId) => {
    const c = currencies.value.find(x => String(x.id) === String(currencyId));
    const mark = c ? (c.symbol || c.code || '') : '';
    return (v === null || v === undefined || v === '') ? '—' : (mark ? mark + ' ' : '') + fmtAmountOnly(v, true);
  };
  async function loadTiers(){
    tiersLoading.value = true; tiersErr.value = '';
    try {
      // a tier is priced in its own currency: load the currency list once to show each tier's symbol
      const [r, cur] = await Promise.all([api.get('/admin/tiers'),
        currencies.value.length ? null : api.get('/admin/currencies?per_page=200').catch(() => null)]);
      if(cur) currencies.value = (cur && cur.data) || [];
      tiers.value = (r && r.data) || (Array.isArray(r) ? r : []);
    }
    catch(e){ tiers.value = []; tiersErr.value = (e.data && e.data.error) || e.message || t('billing.tiers_load_failed', 'Could not load tiers'); }
    tiersLoading.value = false;
  }
  function selectTab(k){
    tab.value = k;
    view.value = 'list';
    if(k === 'tiers') loadTiers();
  }

  // ---- Detail panel ----
  const detailId = signal(null);
  const detailLoading = signal(false), detailErr = signal('');
  const detailSub = signal(null), detailCompany = signal(null), detailTier = signal(null);
  const detailInvoices = signal([]), detailPayments = signal([]), detailHistory = signal([]);
  let detailSeq = 0;

  async function loadDetail(sid){
    const seq = ++detailSeq;
    detailLoading.value = true; detailErr.value = '';
    try {
      // each invoice is in its own currency: the currency list gives its mark (owner 2026-10-19)
      const [r, cur] = await Promise.all([api.get('/admin/billing/' + sid),
        currencies.value.length ? null : api.get('/admin/currencies?per_page=200').catch(() => null)]);
      if(seq !== detailSeq) return;
      if(cur) currencies.value = (cur && cur.data) || [];
      detailSub.value = r.subscription || null;
      detailCompany.value = r.company || null;
      detailTier.value = r.tier || null;
      detailInvoices.value = r.invoices || [];
      detailPayments.value = r.payments || [];
      detailHistory.value = r.tier_log || [];
    } catch(e){
      if(seq !== detailSeq) return;
      detailErr.value = (e.data && e.data.error) || e.message || t('billing.detail_load_failed', 'Could not load this subscription');
    }
    if(seq === detailSeq) detailLoading.value = false;
  }
  function openDetail(row){
    detailId.value = row.id;
    detailLoading.value = true; detailErr.value = '';      // set BEFORE flipping view — no stale-data flash
    reactArmed.value = false; reactErr.value = '';
    ctOpen.value = false; cnOpen.value = false; compOpen.value = false;
    view.value = 'detail';
    loadDetail(row.id);
  }
  function closeDetail(){ view.value = 'list'; }
  async function refreshDetail(){ if(detailId.value != null) await loadDetail(detailId.value); }

  // ---- Change tier modal ----
  const ctOpen = signal(false), ctTierId = signal(''), ctNote = signal(''), ctInvalid = signal({});
  const ctErr = signal(''), ctSaving = signal(false);
  // The picker's OWN options — the tiers priced for THIS subscription's company COUNTRY, not the
  // global Tiers-tab catalogue. Pricing is per-country (billing.tier_product.country_id): every
  // country is seeded a Starter/Growth/Professional/Enterprise ladder, so /admin/tiers (global,
  // country_id NULL) is normally empty and using it here left the picker blank → you couldn't
  // change a tier at all. Load /admin/countries/{id}/tiers; fall back to global for legacy data.
  const ctTiers = signal([]), ctTiersLoading = signal(false), ctTiersErr = signal('');
  async function loadChangeTierOptions(){
    ctTiersLoading.value = true; ctTiersErr.value = ''; ctTiers.value = [];
    const cid = detailCompany.value && detailCompany.value.country_id;
    try {
      let list = [];
      if(cid){
        const r = await api.get('/admin/countries/' + cid + '/tiers?per_page=200');
        list = (r && r.data) || (Array.isArray(r) ? r : []);
      }
      if(!list.length){                                     // no country tiers → shared/global ladder
        const g = await api.get('/admin/tiers?per_page=200');
        list = (g && g.data) || (Array.isArray(g) ? g : []);
      }
      ctTiers.value = list;
    } catch(e){ ctTiers.value = []; ctTiersErr.value = (e.data && e.data.error) || e.message || t('billing.tiers_load_failed', 'Could not load tiers'); }
    ctTiersLoading.value = false;
  }
  function openChangeTier(){
    ctTierId.value = ''; ctNote.value = ''; ctErr.value = ''; ctInvalid.value = {};
    ctOpen.value = true; focusModal();
    loadChangeTierOptions();                                // tiers for this company's country
  }
  function closeChangeTier(){ ctOpen.value = false; }
  async function saveChangeTier(e){
    e.preventDefault();
    if(!ctTierId.value){ ctInvalid.value = { tier: true }; ctErr.value = t('billing.pick_tier_err', 'Please choose a tier.'); return; }
    ctInvalid.value = {}; ctSaving.value = true; ctErr.value = '';
    const body = { tier_id: Number(ctTierId.value) };
    if(ctNote.value.trim()) body.note = ctNote.value.trim();
    try {
      await api.post('/admin/billing/' + detailId.value + '/change-tier', body);
      ctOpen.value = false;
      await refreshDetail(); load();
    } catch(err){ ctErr.value = (err.data && err.data.error) || err.message || t('billing.save_failed', 'Save failed'); }
    ctSaving.value = false;
  }

  // ---- Cancel modal ----
  const cnOpen = signal(false), cnMode = signal('period_end'), cnNote = signal('');
  const cnErr = signal(''), cnSaving = signal(false);
  function openCancel(){
    cnMode.value = 'period_end'; cnNote.value = ''; cnErr.value = '';   // period-end is the RECOMMENDED default
    cnOpen.value = true; focusModal();
  }
  function closeCancel(){ cnOpen.value = false; }
  async function saveCancel(e){
    e.preventDefault();
    cnSaving.value = true; cnErr.value = '';
    const body = { mode: cnMode.value };
    if(cnNote.value.trim()) body.note = cnNote.value.trim();
    try {
      await api.post('/admin/billing/' + detailId.value + '/cancel', body);
      cnOpen.value = false;
      await refreshDetail(); load();
    } catch(err){ cnErr.value = (err.data && err.data.error) || err.message || t('billing.cancel_failed', 'Could not cancel'); }
    cnSaving.value = false;
  }

  // ---- Reactivate — inline 2-click confirm (no modal) ----
  const reactArmed = signal(false), reactBusy = signal(false), reactErr = signal('');
  function armReactivate(){ reactErr.value = ''; reactArmed.value = true; }
  function cancelReactivate(){ reactArmed.value = false; reactErr.value = ''; }
  async function doReactivate(){
    reactBusy.value = true; reactErr.value = '';
    try {
      await api.post('/admin/billing/' + detailId.value + '/reactivate', {});
      reactArmed.value = false;
      await refreshDetail(); load();
    } catch(err){ reactErr.value = (err.data && err.data.error) || err.message || t('billing.reactivate_failed', 'Could not reactivate'); }
    reactBusy.value = false;
  }

  // ---- Grant free / Remove free — confirm modal ----
  const compOpen = signal(false), compTargetEnable = signal(true), compNote = signal('');
  const compErr = signal(''), compSaving = signal(false);
  function openComp(){
    compTargetEnable.value = !(detailSub.value && detailSub.value.is_comp);
    compNote.value = ''; compErr.value = ''; compOpen.value = true; focusModal();
  }
  function closeComp(){ compOpen.value = false; }
  async function saveComp(e){
    e.preventDefault();
    compSaving.value = true; compErr.value = '';
    const body = { enabled: compTargetEnable.value };
    if(compNote.value.trim()) body.note = compNote.value.trim();
    try {
      await api.post('/admin/billing/' + detailId.value + '/comp', body);
      compOpen.value = false;
      await refreshDetail(); load();
    } catch(err){ compErr.value = (err.data && err.data.error) || err.message || t('billing.comp_failed', 'Could not update'); }
    compSaving.value = false;
  }

  // ---- Tier create / edit modal — the real billing.tier_product contract (admin_tiers.py):
  // code* + name* + price_month* required; price_year / caps / sort optional; is_active flag.
  const tmOpen = signal(false), tmId = signal(null);
  const tmCode = signal(''), tmName = signal(''), tmPriceM = signal(''), tmPriceY = signal('');
  const tmMaxTurn = signal(''), tmMaxTxn = signal(''), tmMaxUsers = signal(''), tmSort = signal('');
  const tmActive = signal(true);
  const tmErr = signal(''), tmSaving = signal(false), tmInvalid = signal({});
  function openCreateTier(){
    tmId.value = null; tmCode.value = ''; tmName.value = ''; tmPriceM.value = ''; tmPriceY.value = '';
    tmMaxTurn.value = ''; tmMaxTxn.value = ''; tmMaxUsers.value = ''; tmSort.value = '';
    tmActive.value = true;
    tmErr.value = ''; tmInvalid.value = {}; tmOpen.value = true; focusModal();
  }
  const s = v => (v == null ? '' : String(v));
  function openEditTier(tr){
    tmId.value = tr.id; tmCode.value = tr.code || ''; tmName.value = tr.name || '';
    tmPriceM.value = tr.price_month != null ? String(tr.price_month) : s(tr.price);
    tmPriceY.value = s(tr.price_year);
    tmMaxTurn.value = s(tr.max_turnover); tmMaxTxn.value = s(tr.max_txn_count);
    tmMaxUsers.value = s(tr.max_users); tmSort.value = s(tr.sort_order);
    tmActive.value = tierIsActive(tr);
    tmErr.value = ''; tmInvalid.value = {}; tmOpen.value = true; focusModal();
  }
  const clearTmInvalid = k => { if(tmInvalid.value[k]) tmInvalid.value = { ...tmInvalid.value, [k]: false }; };
  function closeTierModal(){ tmOpen.value = false; }
  async function saveTier(e){
    e.preventDefault();
    const inv = {};
    if(!tmCode.value.trim()) inv.code = true;
    if(!tmName.value.trim()) inv.name = true;
    if(!String(tmPriceM.value).trim()) inv.price_month = true;
    tmInvalid.value = inv;
    if(Object.keys(inv).length){ tmErr.value = t('billing.fill_highlighted', 'Please fill in the highlighted fields.'); return; }
    tmSaving.value = true; tmErr.value = '';
    const body = { code: tmCode.value.trim(), name: tmName.value.trim(),
                   price_month: tmPriceM.value, price_year: tmPriceY.value,
                   max_turnover: tmMaxTurn.value, max_txn_count: tmMaxTxn.value,
                   max_users: tmMaxUsers.value, sort_order: tmSort.value,
                   is_active: tmActive.value };
    try {
      if(tmId.value) await api.put('/admin/tiers/' + tmId.value, body);
      else await api.post('/admin/tiers', body);
      tmOpen.value = false;
      await loadTiers();
    } catch(err){ tmErr.value = (err.data && err.data.error) || err.message || t('billing.save_failed', 'Save failed'); }
    tmSaving.value = false;
  }

  // ---- Tier delete — typed-DELETE confirmation ----
  const tdOpen = signal(false), tdId = signal(null), tdName = signal(''), tdTyped = signal('');
  const tdErr = signal(''), tdDeleting = signal(false);
  function openDeleteTier(tr){
    tdId.value = tr.id; tdName.value = tr.name || ''; tdTyped.value = ''; tdErr.value = '';
    tdOpen.value = true; focusModal();
  }
  function closeDeleteTier(){ tdOpen.value = false; }
  async function doDeleteTier(){
    tdDeleting.value = true; tdErr.value = '';
    try { await api.del('/admin/tiers/' + tdId.value); tdOpen.value = false; await loadTiers(); }
    catch(err){ tdErr.value = (err.data && err.data.error) || err.message || t('billing.delete_failed', 'Delete failed'); }
    tdDeleting.value = false;
  }

  // ---- render: Subscriptions list body (rows + pager) ----
  function subsListBody(){
    if(loading.value) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    if(!rows.value.length) return html`<div class="muted-block">${t('billing.no_subs', 'No subscriptions yet.')}</div>`;
    const pg = () => pager({ page: page.value, totalPages: pages.value, total: total.value,
                             perPage: Number(perPage.value), onPage: goPage });
    return html`<div class="list-wrap">
      ${pg()}
      <div class="row-list">${rows.value.map(s => {
        const endsDate = subEndsDate(s);
        return html`
        <div class="row-card" @click=${() => openDetail(s)}>
          <span class="row-main">
            <b>${s.company}${s.is_demo ? html` <span class="badge badge-warning">${t('billing.demo', 'DEMO')}</span>` : ''}</b>
            <span class="row-sub">${(s.tier || t('billing.no_tier', 'no tier')) + ' · ' + (s.period || '—')
              + (s.started ? ' · ' + t('billing.since', 'since') + ' ' + s.started : '')
              + (s.period_end ? ' · ' + t('billing.renews', 'renews') + ' ' + s.period_end : '')
              + (s.trial_ends ? ' · ' + t('billing.trial_ends', 'trial ends') + ' ' + s.trial_ends : '')}</span>
          </span>
          ${s.provider ? html`<span class="badge badge-light">${s.provider}</span>` : ''}
          ${s.is_comp ? html`<span class="badge badge-primary">${t('billing.free', 'FREE')}</span>` : ''}
          ${endsDate ? html`<span class="badge bl-ends">⏳ ${t('billing.ends', 'ends')} ${endsDate}</span>` : ''}
          <span class=${'badge badge-' + subBadgeClass(s.status)}>${subStatusLabel(s.status)}</span>
          <span class="row-chevron">${icon('chevron-right', {size: 14})}</span>
        </div>`; })}</div>
      ${pg()}
    </div>`;
  }

  // ---- render: Tiers grid body ----
  function tiersGridBody(){
    if(tiersLoading.value) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    if(tiersErr.value) return html`<div class="alert alert-danger py-2">${tiersErr.value}</div>`;
    if(!tiers.value.length) return html`<div class="muted-block">${t('billing.no_tiers', 'No shared (global) tiers.')}<br>
      <span class="muted-sub">${t('billing.tiers_percountry_hint', 'Pricing tiers are set per country — open a country under Countries → Pricing to manage its Starter / Growth / Professional / Enterprise ladder. This tab is only for optional shared tiers that apply across every country.')}</span></div>`;
    return html`<div class="bl-tier-grid">${tiers.value.map(tr => { const on = tierIsActive(tr), caps = tierCapsLine(tr, tierSymbol(tr)); return html`
      <div class="bl-tier-card">
        <div class="bl-tier-head">
          <b>${tr.name}</b>
          ${tr.code ? html`<span class="badge badge-light bl-tier-code">${tr.code}</span>` : ''}
          <span class=${'badge ' + (on ? 'badge-success' : 'badge-secondary')}>${on ? t('common.active', 'Active') : t('billing.inactive', 'Inactive')}</span>
        </div>
        <div class="bl-tier-price">${tierPriceLine(tr, tierSymbol(tr))}</div>
        ${caps ? html`<div class="bl-tier-desc">${caps}</div>` : ''}
        <div class="bl-tier-actions">
          <button type="button" class="icon-btn" title=${t('common.edit', 'Edit')} @click=${() => openEditTier(tr)}>✎</button>
          <button type="button" class="btn btn-sm btn-outline btn-danger-text" @click=${() => openDeleteTier(tr)}>${icon('trash-2', {size: 14})} ${t('common.delete', 'Delete')}</button>
        </div>
      </div>`; })}</div>`;
  }

  // ---- render: Detail panel body ----
  function detailContentBody(){
    if(detailLoading.value) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    if(detailErr.value) return html`<div class="alert alert-danger py-2">${detailErr.value}</div>`;
    const sub = detailSub.value;
    if(!sub) return html`<div class="muted-block">${t('billing.not_found', 'Subscription not found.')}</div>`;
    const invoices = detailInvoices.value, payments = detailPayments.value, history = detailHistory.value;
    const rArmed = reactArmed.value, rBusy = reactBusy.value, rErr = reactErr.value;
    const showReactivate = canReactivate(sub);
    const endsDate = sub.current_period_end || sub.period_end || '';

    return html`
      <div class="bl-detail-badges">
        <span class=${'badge badge-' + subBadgeClass(sub.status)}>${subStatusLabel(sub.status)}</span>
        ${sub.is_comp ? html`<span class="badge badge-primary">${t('billing.free', 'FREE')}</span>` : ''}
        ${sub.cancel_at_period_end && endsDate ? html`<span class="badge bl-ends">⏳ ${t('billing.ends', 'ends')} ${endsDate}</span>` : ''}
      </div>

      <div class="bl-actions">
        <button type="button" class="btn btn-outline" @click=${openChangeTier}>${t('billing.change_tier', 'Change tier')}</button>
        ${sub.status !== 'cancelled' ? html`<button type="button" class="btn btn-outline btn-danger-text" @click=${openCancel}>${t('billing.cancel_sub', 'Cancel subscription')}</button>` : ''}
        ${showReactivate ? (rArmed
            ? html`<span class="confirm-inline">
                <span class="confirm-q">${t('billing.reactivate_q', 'Reactivate?')}</span>
                <button type="button" class="btn-confirm-yes" ?disabled=${rBusy} @click=${doReactivate}>${rBusy ? '…' : t('common.yes', 'Yes')}</button>
                <button type="button" class="btn-confirm-no" @click=${cancelReactivate}>${t('common.no', 'No')}</button>
              </span>`
            : html`<button type="button" class="btn btn-outline" @click=${armReactivate}>${t('billing.reactivate', 'Reactivate')}</button>`
          ) : ''}
        <button type="button" class="btn btn-outline" @click=${openComp}>${sub.is_comp ? t('billing.remove_free', 'Remove free') : t('billing.grant_free', 'Grant free')}</button>
      </div>
      ${rArmed && rErr ? html`<div class="alert alert-danger py-2 mb-2">${rErr}</div>` : ''}

      <div class="detail-grid mb-3">
        <div class="field"><div class="field-label">${t('billing.period', 'Period')}</div><div class="field-val">${sub.period || '—'}</div></div>
        <div class="field"><div class="field-label">${t('billing.period_end', 'Period end')}</div><div class="field-val">${endsDate || '—'}</div></div>
        <div class="field"><div class="field-label">${t('billing.provider', 'Payment provider')}</div><div class="field-val">${sub.provider || '—'}</div></div>
        <div class="field"><div class="field-label">${t('billing.admin_note', 'Admin note')}</div><div class="field-val">${sub.admin_note || '—'}</div></div>
      </div>

      <div class="bl-section">
        <h3 class="bl-section-title">${t('billing.invoices', 'Invoices')}</h3>
        ${!invoices.length ? html`<div class="muted-block">${t('billing.no_invoices', 'No invoices yet.')}</div>` : html`
          <div class="table-wrap"><table class="cb-table">
            <thead><tr><th>${t('billing.number', 'Number')}</th><th>${t('billing.date', 'Date')}</th><th class="text-right">${t('billing.amount', 'Amount')}</th><th>${t('common.status', 'Status')}</th></tr></thead>
            <tbody>${invoices.map(inv => html`<tr>
              <td class="mono">${inv.invoice_no || inv.number || ''}</td><td>${String(inv.issued_at || inv.date || '').slice(0, 10)}</td>
              <td class="mono text-right">${moneyIn(inv.total ?? inv.amount, inv.currency_id)}</td><td>${inv.status}</td>
            </tr>`)}</tbody>
          </table></div>`}
      </div>

      <div class="bl-section">
        <h3 class="bl-section-title">${t('billing.payments', 'Payments')}</h3>
        ${!payments.length ? html`<div class="muted-block">${t('billing.no_payments', 'No payments yet.')}</div>` : html`
          <div class="table-wrap"><table class="cb-table">
            <thead><tr><th>${t('billing.date', 'Date')}</th><th class="text-right">${t('billing.amount', 'Amount')}</th><th>${t('common.status', 'Status')}</th></tr></thead>
            <tbody>${payments.map(p => html`<tr>
              <td>${String(p.paid_at || p.date || '').slice(0, 10)}</td>
              <td class="mono text-right">${moneyIn(p.amount, (invoices.find(i => String(i.id) === String(p.billing_invoice_id)) || {}).currency_id)}</td><td>${p.status}</td>
            </tr>`)}</tbody>
          </table></div>`}
      </div>

      <div class="bl-section">
        <h3 class="bl-section-title">${t('billing.history', 'History')}</h3>
        ${!history.length ? html`<div class="muted-block">${t('billing.no_history', 'No changes yet.')}</div>` : html`
          <div class="table-wrap"><table class="cb-table">
            <thead><tr><th>${t('billing.when', 'When')}</th><th>${t('billing.admin', 'Admin')}</th><th>${t('billing.action', 'Action')}</th><th>${t('common.notes', 'Note')}</th></tr></thead>
            <tbody>${history.map(h => html`<tr>
              <td class="mono">${h.when}</td><td>${h.admin}</td><td>${h.action}</td><td>${h.note || '—'}</td>
            </tr>`)}</tbody>
          </table></div>`}
      </div>
    `;
  }

  // ---- ONE top-level reactive slot for the swappable content (list / grid / detail). Every
  // signal it needs is read inside this call tree — never nested inside ANOTHER ${() => …} slot. ----
  function screenBody(){
    if(view.value === 'detail') return detailContentBody();
    if(tab.value === 'tiers') return tiersGridBody();
    return subsListBody();
  }

  // W9: KPI band + status chips — top-level slots (NOT nested in another ${() => …}); each reads
  // view/tab at the top and returns '' off the subscriptions list, so they never render on the
  // Tiers tab or the detail panel.
  const onSubsList = () => view.value === 'list' && tab.value === 'subs';
  function kpiBand(){
    if(!onSubsList()) return '';
    const s = stats.value, fmt = v => (v === null || v === undefined) ? '—' : String(v);
    const cards = [
      { label: t('billing.kpi_active', 'Active'),       val: s ? fmt(s.active) : '…',    sub: t('billing.kpi_active_sub', 'paying now'),       accent: 'success' },
      { label: t('billing.kpi_trialing', 'Trialing'),   val: s ? fmt(s.trialing) : '…',  sub: t('billing.kpi_trialing_sub', 'in trial'),       accent: 'info' },
      { label: t('billing.kpi_past_due', 'Past due'),   val: s ? fmt(s.past_due) : '…',  sub: t('billing.kpi_past_due_sub', 'payment overdue'), accent: 'warning' },
      { label: t('billing.kpi_cancelled', 'Cancelled'), val: s ? fmt(s.cancelled) : '…', sub: t('billing.kpi_cancelled_sub', 'churned'),       accent: 'danger' },
      { label: t('billing.kpi_total', 'All'),           val: s ? fmt(s.all) : '…',       sub: t('billing.kpi_total_sub', 'subscriptions'),     accent: 'primary' },
    ];
    return html`<div class="kpi-grid">${cards.map(c => html`
      <div class=${'kpi accent-' + c.accent}><div class="card-body">
        <div class="kpi-label">${c.label}</div><div class="kpi-val">${c.val}</div><div class="kpi-sub">${c.sub}</div>
      </div></div>`)}</div>`;
  }
  const BILL_STATUS_FILTERS = [
    ['', t('common.all', 'All')], ['active', t('billing.kpi_active', 'Active')],
    ['trialing', t('billing.kpi_trialing', 'Trialing')], ['past_due', t('billing.kpi_past_due', 'Past due')],
    ['cancelled', t('billing.kpi_cancelled', 'Cancelled')], ['expired', t('billing.status_expired', 'Expired')],
  ];
  function statusChips(){
    if(!onSubsList()) return '';
    const cur = statusFilter.value;
    return html`<div class="adm-chips">${BILL_STATUS_FILTERS.map(([k, label]) => html`<button type="button"
      class=${'btn btn-sm ' + (cur === k ? 'btn-primary' : 'btn-outline')}
      @click=${() => setStatus(k)}>${label}</button>`)}</div>`;
  }

  const content = html`<div class="page">
    <div class="page-head with-action">
      <div>
        <h1 class="page-title">${() => view.value === 'detail'
          ? (detailCompany.value ? detailCompany.value.name : t('billing.subscription', 'Subscription'))
          : t('billing.title', 'Billing')}</h1>
        <div class="page-sub">${() => view.value === 'detail'
          ? ((detailTier.value && detailTier.value.name) || t('billing.no_tier', 'No tier'))
          : (tab.value === 'tiers'
              ? t('billing.tiers_sub', 'Pricing tiers a subscription can be set to')
              : (total.value + (total.value === 1 ? ' ' + t('billing.subscription_one', 'subscription') : ' ' + t('billing.subscription_many', 'subscriptions'))))}</div>
      </div>
      <button type="button" class="btn btn-primary" ?hidden=${() => view.value === 'detail' || tab.value !== 'tiers'}
              @click=${openCreateTier}>${t('billing.add_tier', '+ Add tier')}</button>
    </div>

    <a class="back-link" ?hidden=${() => view.value !== 'detail'} @click=${closeDetail}>${icon('chevron-left', {size: 14})} ${t('billing.back_to_list', 'Subscriptions')}</a>

    <div class="cb-seg bl-tabs" role="group" aria-label=${t('billing.tabs_aria', 'Billing sections')} ?hidden=${() => view.value === 'detail'}>
      <button type="button" class=${() => 'cb-seg-btn' + (tab.value === 'subs' ? ' active' : '')} @click=${() => selectTab('subs')}>${t('billing.tab_subs', 'Subscriptions')}</button>
      <button type="button" class=${() => 'cb-seg-btn' + (tab.value === 'tiers' ? ' active' : '')} ?hidden=${_regionalPartner()} @click=${() => selectTab('tiers')}>${t('billing.tab_tiers', 'Tiers')}</button>
    </div>

    ${kpiBand}

    <div class="toolbar" ?hidden=${() => !(view.value === 'list' && tab.value === 'subs')}>
      <input class="form-control" type="search" placeholder=${t('billing.search_ph', 'Search… e.g. Riverside or Starter')}
        @input=${onSearch}>
      ${perPageSelect(perPage.value, onPer)}
    </div>

    ${statusChips}

    ${screenBody}

    <!-- Change tier -->
    <div class="modal-overlay" ?hidden=${() => !ctOpen.value}>
      <form class="modal-card" @submit=${saveChangeTier}>
        <div class="modal-head"><h3>${t('billing.change_tier', 'Change tier')}</h3><button type="button" class="modal-x" @click=${closeChangeTier}>✕</button></div>
        <label class="form-label">${t('billing.tier', 'Tier')}</label>
        <select class=${() => 'form-control mb-2' + (ctInvalid.value.tier ? ' is-invalid' : '')}
                .value=${ctTierId} @change=${e => { ctTierId.value = e.target.value; if(ctInvalid.value.tier) ctInvalid.value = {}; }}>
          <option value="">${() => ctTiersLoading.value ? t('common.loading', 'Loading…') : t('billing.select_tier', '— Select a tier —')}</option>
          ${() => ctTiers.value.filter(x => tierIsActive(x)).map(x => html`<option value=${x.id}>${x.name}</option>`)}
        </select>
        ${() => ctTiersErr.value ? html`<div class="alert alert-danger py-2 mb-2">${ctTiersErr.value}</div>` : null}
        ${() => (!ctTiersLoading.value && !ctTiersErr.value && !ctTiers.value.length)
          ? html`<div class="muted-block py-2 mb-2">${t('billing.no_tiers_for_country', 'No pricing tiers exist for this company’s country yet — add them from Countries → Pricing.')}</div>` : null}
        <label class="form-label">${t('billing.note_optional', 'Note (optional)')}</label>
        <input class="form-control mb-2" .value=${ctNote} @input=${e => { ctNote.value = e.target.value; }}
               placeholder=${t('billing.note_ph', 'e.g. Requested by owner on support call')}>
        ${() => ctErr.value ? html`<div class="alert alert-danger py-2 mb-2">${ctErr.value}</div>` : null}
        <div class="form-actions">
          <span class="form-actions-spacer"></span>
          <button type="button" class="btn btn-outline" @click=${closeChangeTier}>${t('common.cancel', 'Cancel')}</button>
          <button type="submit" class="btn btn-primary" ?disabled=${() => ctSaving.value}>${() => ctSaving.value ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </div>
      </form>
    </div>

    <!-- Cancel subscription -->
    <div class="modal-overlay" ?hidden=${() => !cnOpen.value}>
      <form class="modal-card" @submit=${saveCancel}>
        <div class="modal-head"><h3>${t('billing.cancel_sub', 'Cancel subscription')}</h3><button type="button" class="modal-x" @click=${closeCancel}>✕</button></div>
        <div class="bl-radio-group">
          <label class=${() => 'bl-radio-card' + (cnMode.value === 'period_end' ? ' active' : '')}>
            <input type="radio" name="cancelMode" value="period_end" .checked=${() => cnMode.value === 'period_end'} @change=${() => { cnMode.value = 'period_end'; }}>
            <span class="bl-radio-card-body">
              <span class="bl-radio-card-title">${t('billing.cancel_period_end', 'At period end')} <span class="badge badge-light">${t('billing.recommended', 'Recommended')}</span></span>
              <span class="bl-radio-card-sub">${() => t('billing.cancel_period_end_sub', 'Keeps access until') + ' ' + (detailSub.value ? (detailSub.value.current_period_end || detailSub.value.period_end || '—') : '—')}</span>
            </span>
          </label>
          <label class=${() => 'bl-radio-card' + (cnMode.value === 'immediate' ? ' active' : '')}>
            <input type="radio" name="cancelMode" value="immediate" .checked=${() => cnMode.value === 'immediate'} @change=${() => { cnMode.value = 'immediate'; }}>
            <span class="bl-radio-card-body">
              <span class="bl-radio-card-title">${t('billing.cancel_immediate', 'Immediately')}</span>
              <span class="bl-radio-card-sub">${t('billing.cancel_immediate_sub', 'Access ends now')}</span>
            </span>
          </label>
        </div>
        <label class="form-label">${t('billing.note_optional', 'Note (optional)')}</label>
        <input class="form-control mb-2" .value=${cnNote} @input=${e => { cnNote.value = e.target.value; }}
               placeholder=${t('billing.cancel_note_ph', 'e.g. Customer requested cancellation')}>
        ${() => cnErr.value ? html`<div class="alert alert-danger py-2 mb-2">${cnErr.value}</div>` : null}
        <div class="form-actions">
          <span class="form-actions-spacer"></span>
          <button type="button" class="btn btn-outline" @click=${closeCancel}>${t('billing.keep_sub', 'Keep subscription')}</button>
          <button type="submit" class="btn btn-danger" ?disabled=${() => cnSaving.value}>${() => cnSaving.value ? t('common.saving', 'Saving…') : t('billing.confirm_cancel', 'Confirm cancel')}</button>
        </div>
      </form>
    </div>

    <!-- Grant / remove free -->
    <div class="modal-overlay" ?hidden=${() => !compOpen.value}>
      <form class="modal-card" @submit=${saveComp}>
        <div class="modal-head"><h3>${() => compTargetEnable.value ? t('billing.grant_free', 'Grant free access') : t('billing.remove_free', 'Remove free access')}</h3><button type="button" class="modal-x" @click=${closeComp}>✕</button></div>
        <div class="form-note">${t('billing.comp_note', 'The company keeps full access and is never charged while free.')}</div>
        <label class="form-label">${t('billing.note_optional', 'Note (optional)')}</label>
        <input class="form-control mb-2" .value=${compNote} @input=${e => { compNote.value = e.target.value; }}
               placeholder=${t('billing.note_ph', 'e.g. Requested by owner on support call')}>
        ${() => compErr.value ? html`<div class="alert alert-danger py-2 mb-2">${compErr.value}</div>` : null}
        <div class="form-actions">
          <span class="form-actions-spacer"></span>
          <button type="button" class="btn btn-outline" @click=${closeComp}>${t('common.cancel', 'Cancel')}</button>
          <button type="submit" class="btn btn-primary" ?disabled=${() => compSaving.value}>${() => compSaving.value ? t('common.saving', 'Saving…') : t('common.confirm', 'Confirm')}</button>
        </div>
      </form>
    </div>

    <!-- Tier create / edit -->
    <div class="modal-overlay" ?hidden=${() => !tmOpen.value}>
      <form class="modal-card" @submit=${saveTier}>
        <div class="modal-head"><h3>${() => tmId.value ? t('billing.edit_tier', 'Edit tier') : t('billing.add_tier_title', 'Add tier')}</h3><button type="button" class="modal-x" @click=${closeTierModal}>✕</button></div>
        <div class="bl-tier-form-grid">
          <div><label class="form-label">${t('billing.tier_code', 'Code')}</label>
          <input class=${() => 'form-control mb-2' + (tmInvalid.value.code ? ' is-invalid' : '')} .value=${tmCode}
                 @input=${e => { tmCode.value = e.target.value.toUpperCase(); clearTmInvalid('code'); }} placeholder="e.g. GROWTH"></div>
          <div><label class="form-label">${t('common.name', 'Name')}</label>
          <input class=${() => 'form-control mb-2' + (tmInvalid.value.name ? ' is-invalid' : '')} .value=${tmName}
                 @input=${e => { tmName.value = e.target.value; clearTmInvalid('name'); }} placeholder=${t('billing.tier_name_ph', 'e.g. Growth')}></div>
          <div><label class="form-label">${t('billing.price_month', 'Price / month')}</label>
          <input class=${() => 'form-control mb-2' + (tmInvalid.value.price_month ? ' is-invalid' : '')} .value=${tmPriceM} inputmode="decimal"
                 @input=${e => { tmPriceM.value = e.target.value; clearTmInvalid('price_month'); }} placeholder=${t('billing.tier_price_ph', 'e.g. 299.00')}></div>
          <div><label class="form-label">${t('billing.price_year', 'Price / year (optional)')}</label>
          <input class="form-control mb-2" .value=${tmPriceY} inputmode="decimal"
                 @input=${e => { tmPriceY.value = e.target.value; }} placeholder="e.g. 2990.00"></div>
          <div><label class="form-label">${t('billing.max_txn', 'Max transactions / month')}</label>
          <input class="form-control mb-2" .value=${tmMaxTxn} inputmode="numeric"
                 @input=${e => { tmMaxTxn.value = e.target.value; }} placeholder="e.g. 500 (blank = unlimited)"></div>
          <div><label class="form-label">${t('billing.max_turnover', 'Max annual turnover')}</label>
          <input class="form-control mb-2" .value=${tmMaxTurn} inputmode="decimal"
                 @input=${e => { tmMaxTurn.value = e.target.value; }} placeholder="e.g. 1000000 (blank = unlimited)"></div>
          <div><label class="form-label">${t('billing.max_users', 'Max users')}</label>
          <input class="form-control mb-2" .value=${tmMaxUsers} inputmode="numeric"
                 @input=${e => { tmMaxUsers.value = e.target.value; }} placeholder="e.g. 5 (blank = unlimited)"></div>
          <div><label class="form-label">${t('billing.sort_order', 'Sort order')}</label>
          <input class="form-control mb-2" .value=${tmSort} inputmode="numeric"
                 @input=${e => { tmSort.value = e.target.value; }} placeholder="e.g. 2"></div>
        </div>
        <label class="form-check"><input type="checkbox" .checked=${tmActive} @change=${e => { tmActive.value = e.target.checked; }}> ${t('common.active', 'Active')}</label>
        ${() => tmErr.value ? html`<div class="alert alert-danger py-2 mb-2">${tmErr.value}</div>` : null}
        <div class="form-actions">
          <span class="form-actions-spacer"></span>
          <button type="button" class="btn btn-outline" @click=${closeTierModal}>${t('common.cancel', 'Cancel')}</button>
          <button type="submit" class="btn btn-primary" ?disabled=${() => tmSaving.value}>${() => tmSaving.value ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </div>
      </form>
    </div>

    <!-- Delete tier — typed DELETE confirmation -->
    <div class="modal-overlay" ?hidden=${() => !tdOpen.value}>
      <div class="modal-card">
        <div class="modal-head"><h3>${t('billing.delete_tier', 'Delete tier')}</h3><button type="button" class="modal-x" @click=${closeDeleteTier}>✕</button></div>
        <div class="form-note">${t('billing.delete_tier_warn', 'This cannot be undone. Type DELETE below to confirm.')}</div>
        <div class="field-val mb-2">${tdName}</div>
        <input class="form-control mb-2" .value=${tdTyped} @input=${e => { tdTyped.value = e.target.value; }} placeholder="DELETE">
        ${() => tdErr.value ? html`<div class="alert alert-danger py-2 mb-2">${tdErr.value}</div>` : null}
        <div class="form-actions">
          <span class="form-actions-spacer"></span>
          <button type="button" class="btn btn-outline" @click=${closeDeleteTier}>${t('common.cancel', 'Cancel')}</button>
          <button type="button" class="btn btn-danger" ?disabled=${() => tdTyped.value.trim() !== 'DELETE' || tdDeleting.value}
                  @click=${doDeleteTier}>${() => tdDeleting.value ? t('billing.deleting', 'Deleting…') : t('common.delete', 'Delete')}</button>
        </div>
      </div>
    </div>
  </div>`;

  return shell('billing', t('billing.title', 'Billing'), content);
}
